/*
  The offer page — /pages/used-offer?t=<token>

  Reads the offer through the signed App Proxy, renders it, and on the accept
  click creates the Shopify draft order behind the scenes. Measured 2026-08-21:
  the whole round trip is 2.6 to 2.9 seconds, which is why this holds the member
  on the screen with a spinner instead of promising them an email.

  API (function_app.py):
    GET  /apps/api/offer?t=<token>
         -> {authorized, state:'signin'|'live'|'accepted'|'paid'|'expired'
                              |'declined'|'closed'|'notyours'|'notfound'|'error',
             offer_id, model, serials[], amount, seconds_left, respond_by,
             pay_by, conversion_fee, invoice_url, has_invoice,
             can_pass, reasons[{key,label}], decline_reason,
             photos[{id,serial}]}
    GET  /apps/api/offer_photo?t=<token>&p=<photo id>
         -> the image bytes, or 404. Never 403: a refusal must not tell a
            stranger which offers carry pictures.
    POST /apps/api/accept_offer {token, voltage_230}
         -> {ok, invoice_url, order_name, pay_by, message, replay}
    POST /apps/api/decline_offer {token, reason}
         -> {ok, status:'declined', reason, was_accepted, invoice, message}
    POST /apps/api/offer_paylink {token}
         -> {ok, status:'queued'|'paid'|'no_invoice'|'rate_limited'|'error',
             replay, message}

  FOUR RULES THIS FILE KEEPS.

  1. THE ACCEPT IS A POST, ALWAYS, AND SO IS THE PASS. A GET that commits is the
     dealer no-stock-link failure: scanners and prefetchers follow every URL in a
     message, so either answer behind a link could be given by a machine on the
     member's behalf. The email's "Pass on this offer" button carries `&pass=1`,
     which only OPENS the reason panel — following it changes nothing.

  2. THE BUTTON DISABLES ON THE FIRST CLICK and never re-enables on success. The
     server is idempotent (a second call returns the same invoice), but a button
     that stays live invites a second click that looks like it did something.

  3. A FAILURE IS NOT AN ERROR SCREEN. Once the server has the yes, the unit is
     theirs; if the draft order did not get made the reply still carries ok:true
     with no invoice_url, and the honest sentence is that the invoice is coming by
     email. Never tell them it failed — it did not.

  4. THE PASS PATH IS REACHABLE FROM EVERY SCREEN THAT COMES AFTER THE EMAIL
     (David 2026-08-21). Live, expired, and — the one that matters — ACCEPTED:
     a member who said yes and changed their mind passes here rather than having
     to phone us, and the server cancels the invoice behind it. `can_pass` off
     the server decides, never this file, so the page can never offer a path the
     server would refuse. A REASON IS REQUIRED and the send button stays off
     until one is picked; there is no "I would rather not say" and adding one
     back is a design change, not a UX improvement.
*/
(function () {
  'use strict';

  var PROXY = '/apps/api';
  var body = document.getElementById('puo-body');
  if (!body) { return; }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function token() {
    var m = /[?&]t=([^&]+)/.exec(window.location.search || '');
    return m ? decodeURIComponent(m[1]) : '';
  }

  // The offer email's pass button lands here. It opens the reason panel and
  // commits NOTHING, which is what lets it be a plain link in a message.
  //
  // CONSUMED ON FIRST USE. "Never mind" re-renders the screen the panel came
  // from, and a flag still reading true at that point would re-open the panel
  // the member just backed out of -- a trap they could not leave without
  // editing the URL.
  var PASS_INTENT = /[?&]pass=1(&|$)/.test(window.location.search || '');
  function takePassIntent() {
    var want = PASS_INTENT;
    PASS_INTENT = false;
    return want;
  }

  // The EXPIRY letter's second button ("Take me off the list") lands here with
  // &out=1 and opens the reason panel. Same shape and same reasoning as the pass
  // intent above: it commits NOTHING, because a link that acts on the GET is
  // followed by every mail scanner in the world, and consumed on first use so
  // "never mind" is a door and not a loop.
  var OUT_INTENT = /[?&]out=1(&|$)/.test(window.location.search || '');
  function takeOutIntent() {
    var want = OUT_INTENT;
    OUT_INTENT = false;
    return want;
  }

  // The offer letter's THIRD button ("Not now, offer it to the next person")
  // lands here with &skip=1. Same shape as the two intents above and for the same
  // reason, which matters more here than on either of them: this answer gives a
  // unit away, so a link that acted on the GET would let any mail scanner do it
  // on the member's behalf. Nothing is recorded until they press the button on
  // the confirm panel and the POST goes.
  var SKIP_INTENT = /[?&]skip=1(&|$)/.test(window.location.search || '');
  function takeSkipIntent() {
    var want = SKIP_INTENT;
    SKIP_INTENT = false;
    return want;
  }

  // The last payload the server gave us, so a "never mind" can put the screen
  // back exactly as it was without a second round trip.
  var OFFER = null;

  // The one correct sign-in URL for this store, with the token kept. The theme
  // snippet defaults return_to to the path only, which would drop ?t= and land
  // them back here with no offer to show.
  function signinUrl() {
    return '/customer_authentication/login?return_to=' +
      encodeURIComponent(window.location.pathname + window.location.search);
  }

  function money(a) {
    var n = parseFloat(a);
    if (isNaN(n)) { return ''; }
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function countdownText(secs) {
    if (secs == null) { return ''; }
    if (secs <= 0) { return 'This offer has closed.'; }
    var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
    if (h >= 1) { return h + (h === 1 ? ' hour ' : ' hours ') + m + ' min left'; }
    return m + ' min left';
  }

  // Its own wording rather than countdownText's: "71 hours 12 min left" is a number
  // nobody converts, and this clock is the one deciding whether they act today.
  // THE DAYS BRANCH IS FOR PRE-CUTOVER OFFERS ONLY now that the pay window and the
  // hold are one clock (David 2026-09-03). It stays because those offers really do
  // run to 72 hours and would otherwise print "68 hours"; it stops being reachable
  // on its own once the last of them closes.
  function payLeftText(secs) {
    if (secs == null) { return ''; }
    if (secs <= 0) { return 'The time to pay has run out'; }
    var d = Math.floor(secs / 86400);
    var h = Math.floor((secs % 86400) / 3600);
    var m = Math.floor((secs % 3600) / 60);
    if (d >= 1) { return d + (d === 1 ? ' day ' : ' days ') + h +
                         (h === 1 ? ' hour' : ' hours'); }
    if (h >= 1) { return h + (h === 1 ? ' hour ' : ' hours ') + m + ' min'; }
    return m + (m === 1 ? ' minute' : ' minutes');
  }

  // Ticks the pay clock every minute. It does NOT reload at zero the way the
  // hold clock does: the member may be mid-payment in another tab and blanking
  // the page under them would be the worst possible moment to do it.
  function payTick(secs) {
    var left = secs;
    var t = setInterval(function () {
      var node = document.getElementById('puo-payclock');
      if (!node) { clearInterval(t); return; }
      left -= 60;
      if (left <= 0) {
        node.innerHTML = '<strong>The time to pay has run out.</strong> ' +
          'If you have already paid, ignore this. If not, reply to your offer ' +
          'email and a person will look at it.';
        clearInterval(t);
        return;
      }
      node.innerHTML = '<strong>' + esc(payLeftText(left)) +
        '</strong> to complete your purchase.';
    }, 60000);
  }

  function panel(inner) { return '<div class="puo__panel">' + inner + '</div>'; }

  function simple(title, text, link) {
    body.innerHTML = panel(
      '<h2 class="puo__h2">' + esc(title) + '</h2><p class="puo__fine">' + text + '</p>' +
      (link || ''));
  }

  // ── more than one unit on one offer ───────────────────────────────────────
  // A monoblock pair is ONE offer: one price, one yes, one order over two
  // serials. THE UNITS READ ONE AFTER THE OTHER AND NEVER SIDE BY SIDE. Two
  // columns look like a picker, and picking is not what this page does: the
  // member's quantity answer is a REASON on the pass panel ("I want one, not a
  // pair"), which ends the offer and puts a person on to finding a home for the
  // other monoblock, because breaking a pair strands it. A layout that invites
  // that click by shape would be inviting it for the wrong reason.
  //
  // The offer email lays the same units out the same way, and it has no choice:
  // the house email standards cap the column at 600px, where two columns would
  // be 280px each and the photographs the letter promises would not survive it.
  // Page and letter are read minutes apart and must not be different shapes.
  function unitCount(o) { return (o.serials || []).length; }

  function unitHeading(o) {
    var n = unitCount(o);
    return n > 2 ? 'The units' : (n === 2 ? 'The pair' : 'The unit');
  }

  function bothWord(o) {
    var n = unitCount(o);
    return n === 2 ? 'both' : 'all ' + n;
  }

  function unitBlock(o) {
    var s = o.serials || [];
    // ONE UNIT RENDERS EXACTLY AS IT ALWAYS HAS, down to the markup: nearly
    // every offer is a single and none of this should touch it.
    if (s.length < 2) {
      return '<p class="puo__unit">' + esc(o.model) +
        (s.length ? ' <span class="puo__serial">Serial ' + esc(s[0]) + '</span>' : '') +
        '</p>';
    }
    var rows = '';
    for (var i = 0; i < s.length; i++) {
      rows += '<li>Serial ' + esc(s[i]) + '</li>';
    }
    return '<p class="puo__unit">' +
      (s.length === 2 ? 'A pair of ' : s.length + ' x ') + esc(o.model) +
      '</p><ul class="puo__serials">' + rows + '</ul>';
  }

  // ── the marks, in pictures ────────────────────────────────────────────────
  // David 2026-08-23: "when a unit has defects and we took pictures, how does
  // the person who received an offer see the pictures?" They did not, anywhere.
  // The bench photographs sat on our own server behind Cockpit, the letter named
  // the marks in words, and this page carried no images at all.
  //
  // THE SAME PICTURES THE EMAIL CARRIES, and that is the point of doing both.
  // The letter attaches them (attachments, never inline: an inline image base64s
  // into the body and Gmail clips the body at about 102KB) and says they are
  // also here. A member reading on a phone with attachments collapsed behind a
  // tap has somewhere to actually look, and a member who came back to the page a
  // week later is not hunting through their mail.
  //
  // THEY ARE FETCHED, NOT EMBEDDED. The payload carries ids; each <img> is a
  // signed App Proxy GET that checks the offer token AND the signed-in customer
  // before it hands over a byte. Nothing about a blemish on a unit that is not
  // listed yet is on a public URL.
  //
  // NO LIGHTBOX. The picture opens in a new tab, which is the browser's own
  // zoom, back button and save. A modal here would be a second thing to build,
  // test on a phone and get wrong on the one screen where a member is deciding
  // whether to spend several thousand dollars.
  function photoBlock(o) {
    var ps = o.photos || [];
    if (!ps.length) { return ''; }
    var t = token();
    // THE SERIAL CAPTION IS PRINTED ONLY WHEN IT DISAMBIGUATES. One amplifier
    // needs no label and the repetition reads as clutter; a pair needs it under
    // every frame, because one gallery covers two units and a scuff means
    // nothing until you know which amp it is on.
    var serials = {};
    for (var j = 0; j < ps.length; j++) {
      if (ps[j].serial) { serials[ps[j].serial] = 1; }
    }
    var label = Object.keys(serials).length > 1;
    var shots = '';
    for (var i = 0; i < ps.length; i++) {
      var url = PROXY + '/offer_photo?t=' + encodeURIComponent(t) +
                '&p=' + encodeURIComponent(ps[i].id);
      // The alt text says what the picture IS, never "photo 2 of 3": somebody on
      // a screen reader is being told what we found on the amplifier.
      var alt = 'What we photographed on this unit' +
                (ps[i].serial ? ', serial ' + ps[i].serial : '');
      shots += '<a class="puo__shot" href="' + esc(url) + '" target="_blank" ' +
        'rel="noopener"><img src="' + esc(url) + '" alt="' + esc(alt) + '" ' +
        'loading="lazy">' +
        (label && ps[i].serial
          ? '<span class="puo__shotcap">Serial ' + esc(ps[i].serial) + '</span>'
          : '') +
        '</a>';
    }
    return '<p class="puo__fine puo__shotlead">Any unit with wear is ' +
      'photographed. ' + (ps.length > 1 ? 'These are' : 'This is') +
      ' what we found on ' + (label ? 'them' : 'it') +
      '. Tap ' + (ps.length > 1 ? 'one' : 'it') + ' to see it full size.</p>' +
      '<div class="puo__shots">' + shots + '</div>';
  }

  // ── passing ───────────────────────────────────────────────────────────────

  function passButton(label) {
    return '<button type="button" class="puo__quiet" id="puo-pass">' +
      esc(label) + '</button>';
  }

  function wirePass(o) {
    var b = document.getElementById('puo-pass');
    if (b) { b.addEventListener('click', function () { openPass(o); }); }
  }

  function reasonLabel(o, key) {
    var rs = o.reasons || [];
    for (var i = 0; i < rs.length; i++) {
      if (rs[i].key === key) { return rs[i].label; }
    }
    return '';
  }

  function openPass(o) {
    var accepted = o.state === 'accepted';
    var rows = (o.reasons || []).map(function (r, i) {
      return '<label class="puo__check"><input type="radio" name="puo-reason" ' +
        'value="' + esc(r.key) + '" id="puo-r' + i + '">' +
        '<span>' + esc(r.label) + '</span></label>';
    }).join('');
    if (!rows) {
      simple('We cannot take that here just now',
        'Reply to the offer email and a person will record it for you.');
      return;
    }
    body.innerHTML = panel(
      '<h2 class="puo__h2">Passing on this one</h2>' +
      unitBlock(o) +
      // CONDITION is one of the reasons on this panel, so the pictures stay in
      // front of them while they pick it. A member passing over a mark they can
      // see is telling us something we can act on; one passing over a mark they
      // only read about may be passing over a sentence.
      photoBlock(o) +
      // NOT A PENALTY, said where the member is deciding. The Standard Terms
      // make the same promise and this is the moment it matters.
      '<p class="puo__fine" style="margin-top:10px">Passing costs you nothing. We ask ' +
      'for the reason because it changes what we show you next, not because it counts ' +
      'against you.</p>' +
      // NO NEVER-AGAIN SENTENCE, BECAUSE THERE IS NO NEVER-AGAIN RULE (David
      // 2026-09-11). A pass used to retire the serial for the member and this
      // panel said so in bold, with a discontinued-model line beside it. The
      // rule is dropped, so the same unit can come back to them, and a sentence
      // promising otherwise would be a consequence we no longer impose. Do not
      // restore a consequence line here without a rule behind it.
      (accepted
        ? '<p class="puo__fine" style="margin-top:10px">You said yes to this one, so ' +
          'passing now cancels the invoice we created for you. Nothing has been ' +
          'charged.</p>'
        : '') +
      '<div style="margin-top:16px">' + rows + '</div>' +
      '<button class="puo__btn" id="puo-send" disabled>Send my answer</button>' +
      '<button type="button" class="puo__quiet" id="puo-never">Never mind</button>' +
      '<p class="puo__note" id="puo-note"></p>');

    var send = document.getElementById('puo-send');
    // Bound per radio rather than delegated from body: body survives every
    // innerHTML swap, so a delegated listener would stack up one per visit.
    var radios = body.querySelectorAll('input[name="puo-reason"]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () { send.disabled = false; });
    }
    send.addEventListener('click', function () { decline(o); });
    document.getElementById('puo-never')
      .addEventListener('click', function () { render(o); });
  }

  // ONE TAP, ONE ANSWER, NO RADIO LIST. The member has already chosen in the
  // letter; this screen exists to be the click that the link deliberately is not,
  // and to say the cost out loud before they make it. If SKIP_UNIT is not on the
  // server's list for this offer -- a post-hold screen, a closed offer -- we fall
  // back to the full reason panel rather than posting a slug the server refuses.
  function openSkip(o) {
    if (!reasonLabel(o, 'SKIP_UNIT')) { openPass(o); return; }
    body.innerHTML = panel(
      '<h2 class="puo__h2">Passing this one along</h2>' +
      unitBlock(o) +
      '<p class="puo__fine" style="margin-top:10px">We will offer this one to the ' +
      'next person waiting, straight away, and you stay on the list for ' +
      esc(o.model) + '.</p>' +
      // THE COST, SAID BEFORE THE CLICK AND IN THE LETTER'S OWN WORDS (David
      // 2026-09-15). A skip re-dates their wait, so they fall behind anybody who
      // has been waiting longer than this moment. Saying only "you keep your
      // place" would be the flattering half of a true sentence.
      //
      // AND THE REST IT BUYS THEM (David 2026-09-16). A skip now holds this model
      // in this color for skip_days, so "we will write about the next one" stopped
      // being true on its own and needed the clause in front of it. His reason for
      // the rest is the member's experience of it -- "we may alienate them if they
      // are the only one eligible so they keep getting more units machine gun
      // style" -- which is why it is said out loud here rather than run quietly.
      //
      // THE PHRASE AND THE NUMBER BOTH COME OFF THE SERVER (o.skip_rest,
      // o.skip_days) AND ARE NEVER BUILT HERE. This sentence also exists in the
      // offer letter, in the pass page's choice label and in the reply the decline
      // returns; a browser assembling its own copy from model and finish is how
      // the fourth one starts saying something different from the other three. An
      // older payload without the keys falls back to the sentence as it read
      // before this change: true, just less.
      (o.skip_rest && o.skip_days
        ? '<p class="puo__fine" style="margin-top:10px">We will rest the ' +
          esc(o.skip_rest) + ' for ' + esc(String(o.skip_days)) + ' days so you ' +
          'are not hearing from us about the same amplifier twice in a week, and ' +
          'write to you after that, behind anyone who has been waiting longer. ' +
          'Everything else on your list is unchanged.</p>'
        : '<p class="puo__fine" style="margin-top:10px">We will write to you ' +
          'about the next one, after anyone who has been waiting longer.</p>') +
      '<button class="puo__btn" id="puo-skip-go">Yes, offer it to the next ' +
      'person</button>' +
      '<button type="button" class="puo__quiet" id="puo-never">Never mind</button>' +
      '<p class="puo__note" id="puo-note"></p>');
    document.getElementById('puo-skip-go')
      .addEventListener('click', function () { sendSkip(o); });
    document.getElementById('puo-never')
      .addEventListener('click', function () { render(o); });
  }

  function sendSkip(o) {
    var go = document.getElementById('puo-skip-go');
    var note = document.getElementById('puo-note');
    go.disabled = true;
    note.className = 'puo__note';
    note.innerHTML = '<span class="puo__spin"></span>Recording your answer&hellip;';
    fetch(PROXY + '/decline_offer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token(), reason: 'SKIP_UNIT' })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, j: j }; },
        function () { return { ok: false, j: {} }; });
    }).then(function (res) {
      if (res.j && res.j.ok) { passed(res.j); return; }
      note.className = 'puo__note puo__note--bad';
      note.textContent = (res.j && (res.j.message || res.j.error)) ||
        'We could not record that. Your offer is unchanged. Please try again.';
      go.disabled = false;
    }).catch(function () {
      note.className = 'puo__note puo__note--bad';
      note.textContent = 'That did not go through. Your offer is unchanged. ' +
        'Please try again in a moment.';
      go.disabled = false;
    });
  }

  function decline(o) {
    var send = document.getElementById('puo-send');
    var note = document.getElementById('puo-note');
    var picked = body.querySelector('input[name="puo-reason"]:checked');
    if (!picked) { return; }
    send.disabled = true;
    note.className = 'puo__note';
    note.innerHTML = '<span class="puo__spin"></span>Recording your answer&hellip;';

    fetch(PROXY + '/decline_offer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token(), reason: picked.value })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, j: j }; },
        function () { return { ok: false, j: {} }; });
    }).then(function (res) {
      if (res.j && res.j.ok) { passed(res.j); return; }
      note.className = 'puo__note puo__note--bad';
      note.textContent = (res.j && (res.j.message || res.j.error)) ||
        'We could not record that. Your offer is unchanged. Please try again.';
      if (res.j && res.j.error === 'server_error') { send.disabled = false; }
    }).catch(function () {
      note.className = 'puo__note puo__note--bad';
      note.textContent = 'That did not go through. Your offer is unchanged. ' +
        'Please try again in a moment.';
      send.disabled = false;
    });
  }

  function passed(res) {
    // NO SPECIFIC PROMISE ABOUT WHAT HAPPENS NEXT. Each reason is meant to carry
    // an action, and those actions are not built yet, so a sentence naming one
    // would be a promise a human has to remember to keep. This says the true
    // thing that holds for every reason on the list.
    var extra = '';
    if (res.was_accepted) {
      extra = res.invoice === 'deleted'
        ? '<p class="puo__fine" style="margin-top:10px">The invoice we created for you ' +
          'has been canceled. Nothing was charged.</p>'
        : '<p class="puo__fine" style="margin-top:10px">We are canceling the invoice we ' +
          'created for you. Nothing has been charged, and you can ignore it.</p>';
    }
    body.innerHTML = panel(
      '<h2 class="puo__h2">Thank you for telling us</h2>' +
      '<p class="puo__lede" style="margin:0 0 6px">' +
      esc(res.message === 'Thank you for telling us.'
        ? 'That is recorded. It changes what we show you next rather than counting ' +
          'against you.'
        : (res.message || 'That is recorded.')) + '</p>' + extra +
      '<p class="puo__fine" style="margin-top:10px">The unit goes to the next person ' +
      'waiting for one.</p>');
    // NO serial_note (David 2026-09-11). It repeated the never-again sentence
    // after the click; that rule is dropped and the server no longer sends it.
  }

  // ── after the hold has run out ────────────────────────────────────────────
  // The two answers the expiry letter offers. The offer itself is over and this
  // token cannot reopen it: nothing on these screens can accept a unit, because
  // the unit has already gone to the next person in line.
  //
  // THE FAVOR IS ONCE EVER AND ITS STATE IS SHOWN BEFORE IT IS SPENT. Finding
  // out you have already used yours AFTER clicking is the version that makes
  // somebody feel tricked, so `favour_available` is rendered into the button's
  // own paragraph rather than only into the confirmation.

  function postHold(o) {
    var gone = o.favour_available === false;
    body.innerHTML = panel(
      '<h2 class="puo__h2">The hold on this one has ended</h2>' +
      unitBlock(o) +
      '<p class="puo__fine" style="margin-top:10px">It has gone to the next person ' +
      'in line. Nothing is held against you for it, and you keep your place on the ' +
      'list for this model.</p>' +
      (gone
        // NAMED, NOT JUST REFUSED. "You have used yours" with no detail invites a
        // reply asking when, and the answer is already in our hands.
        ? '<p class="puo__fine" style="margin-top:16px">We can move somebody to the ' +
          'front of the line once, ever. Yours went on the ' +
          esc(o.favour_spent_on || 'an earlier offer') + ', so we cannot do it ' +
          'again. Telling us you are still interested still lifts the rest ' +
          'on your name.</p>' +
          '<button type="button" class="puo__btn" id="puo-next">I am still ' +
          'interested</button>'
        : '<p class="puo__fine" style="margin-top:16px">If you have only just seen ' +
          'this, we will put you first for the next one instead of resting your ' +
          'name. We can do that once for you, ever, so that it stays fair to ' +
          'everybody else waiting for the same amplifier.</p>' +
          '<button type="button" class="puo__btn" id="puo-next">I just saw this. ' +
          'Put me next.</button>') +
      '<button type="button" class="puo__quiet" id="puo-out">Take me off the ' +
      'list</button>' +
      '<p class="puo__note" id="puo-note"></p>');
    document.getElementById('puo-next')
      .addEventListener('click', function () { sendPostHold(o, 'NEXT', null); });
    document.getElementById('puo-out')
      .addEventListener('click', function () { openOut(o); });
  }

  function openOut(o) {
    var rows = (o.reasons || []).map(function (r, i) {
      return '<label class="puo__check"><input type="radio" name="puo-reason" ' +
        'value="' + esc(r.key) + '" id="puo-o' + i + '">' +
        '<span>' + esc(r.label) + '</span></label>';
    }).join('');
    if (!rows) {
      simple('We cannot take that here just now',
        'You can manage your Used Watch List from your account at any time.');
      return;
    }
    body.innerHTML = panel(
      '<h2 class="puo__h2">Taking this one off your list</h2>' +
      unitBlock(o) +
      // WHY WE ASK, said where they are deciding whether to answer it. The
      // reason is required, so the sentence explaining it has to be honest
      // about what it is for rather than softening it into an optional survey.
      '<p class="puo__fine" style="margin-top:10px">You asked us to watch for this ' +
      'model, so tell us which one it is. It is the only thing we get back, and it ' +
      'changes what we put in front of you next.</p>' +
      '<div style="margin-top:16px">' + rows + '</div>' +
      '<button class="puo__btn" id="puo-send" disabled>Take it off my list</button>' +
      '<button type="button" class="puo__quiet" id="puo-never">Never mind</button>' +
      '<p class="puo__note" id="puo-note"></p>');
    var send = document.getElementById('puo-send');
    var radios = body.querySelectorAll('input[name="puo-reason"]');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () { send.disabled = false; });
    }
    send.addEventListener('click', function () {
      var picked = body.querySelector('input[name="puo-reason"]:checked');
      if (picked) { sendPostHold(o, 'OUT', picked.value); }
    });
    document.getElementById('puo-never')
      .addEventListener('click', function () { postHold(o); });
  }

  function sendPostHold(o, answer, reason) {
    var note = document.getElementById('puo-note');
    var buttons = body.querySelectorAll('button');
    for (var i = 0; i < buttons.length; i++) { buttons[i].disabled = true; }
    note.className = 'puo__note';
    note.innerHTML = '<span class="puo__spin"></span>Recording your answer&hellip;';

    fetch(PROXY + '/offer_post_hold', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token(), answer: answer, reason: reason })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, j: j }; },
        function () { return { ok: false, j: {} }; });
    }).then(function (res) {
      if (res.j && res.j.ok) {
        body.innerHTML = panel(
          '<h2 class="puo__h2">Thank you for telling us</h2>' +
          '<p class="puo__lede" style="margin:0 0 6px">' +
          esc(res.j.message || 'That is recorded.') + '</p>');
        return;
      }
      note.className = 'puo__note puo__note--bad';
      note.textContent = (res.j && (res.j.message || res.j.error)) ||
        'We could not record that. Nothing has changed. Please try again.';
      for (var k = 0; k < buttons.length; k++) { buttons[k].disabled = false; }
    }).catch(function () {
      note.className = 'puo__note puo__note--bad';
      note.textContent = 'That did not go through. Nothing has changed. ' +
        'Please try again in a moment.';
      for (var k = 0; k < buttons.length; k++) { buttons[k].disabled = false; }
    });
  }

  function render(o) {
    var state = o.state;
    OFFER = o;

    // THE SIGN-IN SCREEN IS A WALL AND THE COPY IS THE ONLY DOOR THROUGH IT.
    // The server has always refused a signed-out accept (401) and a stranger
    // (403), and that is right; what this screen owed the member was WHICH
    // account, and why there is one at all.
    //
    // THE MASKED ADDRESS IS FOR ALIASES AND FORWARDS (David 2026-08-28: "if
    // someone uses aliases and forwarding they may not know the account"). The
    // letter names the address in full because the letter is already sitting in
    // that mailbox; this page rides a token that travels just as well in a
    // forward, so it names enough to RECOGNIZE and not enough to use. Absent
    // hint just drops the line -- an older function app returns no field, and a
    // blank line beats a broken one.
    //
    // AND "signing in creates your account" IS NOT FILLER: hosted customer
    // accounts have no separate registration, so a member who has never signed
    // in reads a bare "Sign in" as "I have no account here" and closes the tab.
    if (state === 'signin') {
      body.innerHTML = panel(
        '<h2 class="puo__h2">Sign in to see it</h2>' +
        '<p class="puo__fine">Your offer is held against your account, so nobody ' +
        'else can take it. Signing in brings you straight back to this page.</p>' +
        (o.email_hint
          ? '<p class="puo__fine">Sign in as <strong>' + esc(o.email_hint) +
            '</strong>, the address this offer was sent to. Another address will ' +
            'not open it, even if the mail reached you there.</p>'
          : '') +
        '<p class="puo__fine">If you have never signed in before, signing in ' +
        'creates your account. There is no separate sign-up to do first.</p>' +
        '<a class="puo__btn" href="' + esc(signinUrl()) + '">Sign in</a>');
      return;
    }
    if (state === 'notfound') {
      simple('We could not find this offer',
        'The link may be incomplete. Reply to the offer email and a person will look at it.');
      return;
    }
    // THE WRONG-ACCOUNT SCREEN NAMED THE PROBLEM AND NOT THE FIX. "Sign in with
    // the address the offer was sent to" is unanswerable for somebody whose
    // mailbox collects three aliases, which is exactly the person who lands
    // here: the ones who arrive on the right account never see this screen.
    if (state === 'notyours') {
      simple('This offer belongs to another account',
        'You are signed in as someone else.' +
        (o.email_hint
          ? ' Sign out and sign back in as <strong>' + esc(o.email_hint) +
            '</strong>, the address this offer was sent to.'
          : ' Sign in with the address the offer was sent to.'),
        '<a class="puo__btn" href="' + esc(signinUrl()) + '">Sign in as someone else</a>');
      return;
    }
    if (state === 'expired') {
      // A LATE ANSWER IS STILL AN ANSWER. The hold is gone and the unit with it,
      // but the REASON is the part that shapes what they are shown next, so the
      // pass path stays open here on purpose.
      simple('This offer has closed',
        'The hold has run out and the unit has gone to the next person watching. ' +
        'Nothing is held against you for it, and you stay on the list for the next one.',
        o.can_pass ? passButton('Tell us why you passed') : '');
      wirePass(o);
      return;
    }
    // THE POST-HOLD SCREEN COMES BEFORE 'closed', and that ordering is the whole
    // point of the state: the offer IS closed, so a check on status alone would
    // show somebody who clicked "put me next" a dead end instead of the thing
    // they were promised in the letter.
    if (state === 'posthold') {
      if (o.post_hold_answer) {
        // Already answered. Replayed rather than re-offered, so a second visit
        // cannot spend a favor twice or re-remove a watch they have since
        // deliberately added back.
        simple(o.post_hold_answer === 'NEXT' ? 'We have your answer'
                                             : 'Taken off your list',
          o.post_hold_answer === 'NEXT'
            ? 'You told us you were still interested, and the rest on your name is ' +
              'lifted. Nothing more to do.'
            : 'You asked us to take this model off your Used Watch List. You can ' +
              'add it back from your watch list page at any time.');
        return;
      }
      if (takeOutIntent()) { openOut(o); return; }
      postHold(o);
      return;
    }
    if (state === 'closed') {
      simple('This offer has closed',
        'Nothing is held against you for it, and you stay on the list for the next one.');
      return;
    }
    if (state === 'declined') {
      var said = reasonLabel(o, o.decline_reason);
      simple('You passed on this one',
        'Thank you for telling us why. Nothing is held against you for it, and the ' +
        'unit has gone to the next person waiting.' +
        (said ? '<br><span class="puo__serial">You said: ' + esc(said) + '</span>' : ''));
      return;
    }
    if (state === 'error') {
      simple('Something went wrong at our end',
        'Your offer is unchanged. Try again in a moment, or reply to the offer email.');
      return;
    }
    if (state === 'paid') {
      simple('Paid, and thank you',
        'This one is settled. We will be in touch as it ships.');
      return;
    }
    if (state === 'accepted') {
      // They already said yes: this is a revisit, not a second acceptance. The
      // pay clock comes along, or a member who came back to check how long they
      // have would be shown a date and left to work it out.
      done({ invoice_url: o.invoice_url, pay_by: o.pay_by,
             pay_seconds_left: o.pay_seconds_left }, true);
      return;
    }

    // live
    var n = unitCount(o);
    var many = n > 1;
    // THE CONVERSION IS PER UNIT and the acceptance charges it per unit, so a
    // pair is quoted the total with the each-figure beside it. Quoting $100 over
    // two amps would understate the invoice by $100 on the page that took the
    // yes.
    var feeEach = parseFloat(o.conversion_fee || '100.00');
    var fee = many ? money(String(feeEach * n)) + ' (' + money(String(feeEach)) +
                     ' each)'
                   : money(o.conversion_fee || '100.00');
    body.innerHTML =
      panel(
        '<h2 class="puo__h2">' + unitHeading(o) + '</h2>' +
        unitBlock(o) +
        // ABOVE THE PRICE, DELIBERATELY. This is the condition of the goods and
        // it belongs in front of the number, not underneath it where a reader
        // who has already decided would scroll past it.
        photoBlock(o) +
        '<div class="puo__priceline"><span class="puo__pricelabel">' +
        (many ? 'Your price for ' + bothWord(o) : 'Your price') + '</span>' +
        '<span class="puo__price">' + money(o.amount) + '</span></div>' +
        // WHERE THE CONVERSION SHOWS UP AS MONEY. Empty until the box is ticked,
        // and it sits under the price rather than beside the checkbox because
        // this is the panel a member reads to find out what they are paying.
        '<div id="puo-230line" hidden></div>' +
        // NEVER "SHIPPING IS INCLUDED" AND NEVER "FREE FREIGHT" (David
        // 2026-08-21). Shipping is only waived above the $4,500 threshold, so
        // the old line promised a cheaper unit something checkout then rated.
        '<p class="puo__fine">Standard shipping policy applies. Tax is added at checkout.</p>' +
        '<p class="puo__clock" id="puo-clock"><strong>' + esc(countdownText(o.seconds_left)) +
        '</strong> on this hold.</p>') +
      panel(
        '<h2 class="puo__h2">Saying yes</h2>' +
        '<label class="puo__check"><input type="checkbox" id="puo-230v">' +
        // Named as the EUROPEAN configuration, David 2026-08-21: a bare "230V" is a
        // number a US reader can misread. Europe is named but is not the whole scope
        // (Australia, the UK and most of Asia are 230V too), so the qualifier stays
        // "as they are in Europe and most countries outside North America".
        // THE CONVERSION IS ALL OR NOTHING ON A PAIR, like everything else on
        // this offer: one price, one yes, one order, and a buy-back that takes
        // both back or neither. A half-converted pair is not a thing we build.
        '<span>Convert ' + (many ? bothWord(o) + ' units' : 'this unit') +
        ' to the European 230V / 50 Hz configuration before ' +
        (many ? 'they ship' : 'it ships') + ', for ' + fee + '. ' +
        (many ? 'They come with European cords.' : 'It comes with a European cord.') +
        ' Choose this only if your ' +
        'mains are 230V, as they are in Europe and most countries outside North ' +
        'America.</span></label>' +
        '<label class="puo__check"><input type="checkbox" id="puo-ack">' +
        '<span>I have read <a class="puo__link" href="/pages/used-watch-how">the terms</a> ' +
        // "We create the invoice", never "we raise" it (David 2026-08-21, in all
        // places). Raising an invoice is our word for what happens on our side;
        // the customer is being told what we do for them.
        'and I want ' + (many ? bothWord(o) : 'this unit') +
        '. Nothing is charged now: we create the invoice and you ' +
        // ONE CLOCK FROM THE CUTOVER (David 2026-09-03). This is the sentence a
        // member ticks a box against immediately before spending several thousand
        // dollars, so it is the last place that may still promise a window we have
        // removed. single_clock comes off the server payload, per offer, because
        // offers sent before the cutover genuinely still have the 72 hours.
        (o.single_clock
          ? 'pay it inside that same hold, not afterward.'
          : 'have 72 hours to pay it, unless this offer says otherwise.') +
        '</span></label>' +
        '<button class="puo__btn" id="puo-yes" disabled>Yes, I want ' +
        (many ? bothWord(o) : 'this unit') + '</button>' +
        (o.can_pass ? passButton('Pass on this offer') : '') +
        '<p class="puo__note" id="puo-note"></p>');

    var ack = document.getElementById('puo-ack');
    var yes = document.getElementById('puo-yes');
    ack.addEventListener('change', function () {
      yes.disabled = !ack.checked;
      // Clears the "the total has changed" line the voltage box leaves behind:
      // they have just re-read it, which is what that line asked for.
      var n = document.getElementById('puo-note');
      if (n && ack.checked) { n.textContent = ''; }
    });
    yes.addEventListener('click', function () { accept(o); });
    // THE CONVERSION CHANGES THE FIGURE, SO THE FIGURE CHANGES ON SCREEN. The
    // fee is charged PER UNIT at acceptance, so on a pair the tick is $200, not
    // $100, and the invoice would otherwise be the first place the member ever
    // saw that number.
    //
    // AND IT TAKES THE YES BACK, BOTH DIRECTIONS (David 2026-08-21: "clicking
    // the voltage change should stop the accept offer until the page has been
    // recast. If it's unselected same thing should occur"). An agreement cannot
    // predate the number it agrees to: somebody who ticks the terms, then ticks
    // 230V, then presses the button would be accepting a total they never read.
    // Unticking is the same act in reverse and is treated the same way.
    var v230 = document.getElementById('puo-230v');
    if (v230) {
      v230.addEventListener('change', function () {
        show230(o, v230.checked);
        ack.checked = false;
        yes.disabled = true;
        var note = document.getElementById('puo-note');
        if (note) {
          note.className = 'puo__note';
          note.textContent = v230.checked
            ? 'The total has changed. Read it and tick the box again.'
            : 'The conversion is off again and the total is back to the offer '
              + 'price. Tick the box again to go ahead.';
        }
      });
    }
    // The pass is NOT behind the acknowledgment tick: agreeing to the terms is a
    // condition of buying, never of turning something down.
    wirePass(o);

    if (o.seconds_left != null) { tick(o.seconds_left); }

    // The email's pass button lands with &pass=1 and opens the panel straight
    // away, after the unit has been rendered behind it so a back-out has
    // somewhere to go.
    //
    // THE SKIP INTENT IS READ ONLY HERE, on the LIVE offer, and that is the whole
    // of its scope by design: every other state returns above this line, so a
    // &skip=1 arriving at an expired, closed or already-answered offer never
    // opens a panel that would promise to pass a unit along that has already
    // gone. It is tested before the pass intent because the two can only both be
    // set by a hand-edited URL, and the narrower answer should win.
    if (takeSkipIntent()) { openSkip(o); return; }
    if (takePassIntent()) { openPass(o); }
  }

  // The 230V arithmetic, drawn under the price when the box is ticked and taken
  // away again when it is not. ONE SOURCE FOR THE FIGURES: conversion_fee is the
  // per-unit fee the offer carries and accept_offer multiplies it by the same
  // unit count, so what is shown here is what the invoice will say.
  function show230(o, on) {
    var el = document.getElementById('puo-230line');
    if (!el) { return; }
    if (!on) { el.hidden = true; el.innerHTML = ''; return; }
    var n = Math.max(1, unitCount(o));
    var each = parseFloat(o.conversion_fee || '100.00');
    var add = each * n;
    var total = (parseFloat(o.amount) || 0) + add;
    el.innerHTML =
      '<div class="puo__priceline puo__priceline--add">' +
      '<span class="puo__pricelabel">230V conversion' +
      (n > 1 ? ' (' + n + ' &times; ' + money(String(each)) + ')' : '') +
      '</span><span class="puo__addfig">+' + money(String(add)) + '</span></div>' +
      '<div class="puo__priceline"><span class="puo__pricelabel">New total</span>' +
      '<span class="puo__price">' + money(String(total)) + '</span></div>';
    el.hidden = false;
  }

  function tick(secs) {
    var left = secs;
    var el = document.getElementById('puo-clock');
    if (!el) { return; }
    setInterval(function () {
      left -= 30;
      var node = document.getElementById('puo-clock');
      if (!node) { return; }
      if (left <= 0) { window.location.reload(); return; }
      node.innerHTML = '<strong>' + esc(countdownText(left)) + '</strong> on this hold.';
    }, 30000);
  }

  function accept(o) {
    var yes = document.getElementById('puo-yes');
    var note = document.getElementById('puo-note');
    var v230 = document.getElementById('puo-230v');
    yes.disabled = true;
    note.className = 'puo__note';
    note.innerHTML = '<span class="puo__spin"></span>Creating your invoice&hellip; ' +
      'this takes a few seconds, please stay on the page.';

    fetch(PROXY + '/accept_offer', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token(), voltage_230: !!(v230 && v230.checked) })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, j: j }; },
        function () { return { ok: false, j: {} }; });
    }).then(function (res) {
      if (res.j && res.j.ok) { done(res.j, false); return; }
      // A real refusal: closed, already answered, wrong account. Say what it is
      // and leave the button off, because none of them are retryable.
      note.className = 'puo__note puo__note--bad';
      note.textContent = (res.j && (res.j.message || res.j.error)) ||
        'We could not complete that. Your offer is unchanged. Please try again.';
      if (res.j && res.j.error === 'server_error') { yes.disabled = false; }
    }).catch(function () {
      note.className = 'puo__note puo__note--bad';
      note.textContent = 'That did not go through. Your offer is unchanged. ' +
        'Please try again in a moment.';
      yes.disabled = false;
    });
  }

  function done(res, revisit) {
    var payby = res.pay_by
      ? new Date(res.pay_by + 'T12:00:00').toLocaleDateString('en-US',
        { weekday: 'long', month: 'long', day: 'numeric' })
      : '';
    var head = revisit ? 'This one is yours' : 'Thank you';
    // A FRESH YES SAYS THE CONFIRMATION IS COMING; A REVISIT DOES NOT. Accepting
    // queues one (function_app writes CONFIRM_REQ, job 153 sends it within about
    // five minutes) and until 2026-08-21 nothing was emailed on a yes at all --
    // Brett accepted a $6,025 purchase and had nothing in writing. On a REVISIT
    // that mail has already gone, so repeating the promise would send a member
    // hunting a second time for a message that is already in their inbox.
    // OFFER is the payload this page rendered from, so the thank-you can say
    // "both" where the offer carried a pair. Read defensively: done() also runs
    // on a revisit, where the wording does not depend on it.
    var manyDone = OFFER && unitCount(OFFER) > 1;
    var lead = revisit
      ? 'You accepted this offer. The invoice is created and waiting for you.'
      : (manyDone
        ? 'They are yours to complete. We have created the invoice in your name, ' +
          'with the serial numbers of those exact units on it. A confirmation is ' +
          'on its way to your inbox.'
        : 'The unit is yours to complete. We have created the invoice in your name, ' +
          'with the serial number of that exact unit on it. A confirmation is on ' +
          'its way to your inbox.');

    // The email is not a button. It goes either way, because the promise on the
    // how-page is that we create the invoice straight away and somebody who closes
    // this tab still needs the link. So the second action is only "later".
    // THE PAY WINDOW COUNTS DOWN (David 2026-08-21: "pay later should give a
    // specific timer ... you have XX hours remaining to finalize your
    // purchase"). The seconds come from the SERVER, measured to the end of the
    // pay-by day in Pacific, because that is where the window actually ends.
    // Building the deadline here from the date string would put it at local
    // midnight and hand an east-coast buyer three hours less than they have.
    var secs = res.pay_seconds_left;
    // ON THE SINGLE CLOCK THE DATE IS NOT THE DEADLINE (David 2026-09-03). PayByDate
    // is still written, to the Pacific DAY the hold ends on, but the hold ends at an
    // INSTANT inside that day. "by the end of Monday" beside a countdown reading two
    // hours is the page contradicting itself, and the generous half is the wrong one
    // to believe. The countdown is exact, so on this regime it stands alone.
    var oneClock = !!(OFFER && OFFER.single_clock);
    var html =
      '<h2 class="puo__h2">' + esc(head) + '</h2>' +
      '<p class="puo__lede" style="margin:0 0 6px">' + esc(lead) + '</p>' +
      (secs != null
        ? '<p class="puo__clock" id="puo-payclock"><strong>' +
          esc(payLeftText(secs)) + '</strong> to complete your purchase' +
          (payby && !oneClock ? ', by the end of ' + esc(payby) : '') + '.</p>'
        : (oneClock
            ? '<p class="puo__fine">Payment is due before the hold on this offer ' +
              'runs out.</p>'
            : (payby ? '<p class="puo__fine">Payment is due by ' + esc(payby) +
               ', unless this offer says otherwise.</p>' : '')));

    // SAYING YES IS NOT THE END OF THE PASS PATH (David 2026-08-21). A member who
    // changes their mind after accepting passes from this screen, and the server
    // cancels the invoice behind it. Marked here rather than read off the stale
    // payload, because after a fresh accept the server still called this offer
    // 'live' when it answered.
    if (OFFER && !revisit) { OFFER.state = 'accepted'; OFFER.can_pass = true; }

    if (res.invoice_url) {
      // "I WILL PAY LATER" IS THE WRONG WORD ON A CLOCK THIS SHORT. The action is
      // unchanged and still worth having -- it emails the invoice link to somebody
      // who is about to close the tab -- but "later" now suggests a window that no
      // longer exists, and this button sits directly under a countdown that may read
      // two hours. It is named for what it does instead.
      html += '<a class="puo__btn" href="' + esc(res.invoice_url) + '">Pay now</a>' +
        '<button type="button" class="puo__quiet" id="puo-later">' +
        (oneClock ? 'Email me the link' : 'I will pay later') + '</button>' +
        '<p class="puo__note" id="puo-note2"></p>';
    } else {
      // The accept landed but the invoice did not get created. TRUE, and not an
      // error: it is coming, by hand if it has to. NO TIMER ON IT. Nothing
      // automatic retries this one -- it lands on the offers board as
      // DRAFT_FAILED for a person -- so "within a few minutes" was a promise
      // with nobody behind it, the same fault as the old pay-later sentence.
      html += '<p class="puo__fine" style="margin-top:14px">' +
        esc(res.message || 'Your invoice is being created and we will email you ' +
          'the link as soon as it is ready. The unit is held for you either way.') +
        '</p>';
    }
    if (OFFER && OFFER.can_pass) {
      html += '<p class="puo__fine" style="margin-top:18px">Changed your mind? ' +
        'You can still pass on this one and we will cancel the invoice.</p>' +
        passButton('Pass on this offer');
    }
    body.innerHTML = panel(html);

    var later = document.getElementById('puo-later');
    if (later) {
      later.addEventListener('click', function () {
        // "I WILL PAY LATER" NOW SENDS SOMETHING, and that is the whole point of
        // this handler. The original copy said the link to pay was in their
        // email; it never was, because draftOrderCreate mails nobody and
        // draftOrderInvoiceSend is called nowhere. Brett looked for that email on
        // 2026-08-21 and found nothing, and David's ruling was that it should say
        // "on its way to your inbox" — a promise, so offer_paylink exists to keep
        // it. THE SENTENCE AND THE SEND GO TOGETHER: never restore one without
        // the other.
        //
        // The wording is FUTURE TENSE on purpose. The function app cannot send
        // mail; it records the ask and the pay-window job on DBS sends it within
        // minutes. "We sent it" would be false for those minutes, which is
        // exactly the window a member spends staring at their inbox.
        var n = document.getElementById('puo-note2');
        var btn = this;
        btn.disabled = true;
        n.className = 'puo__note';
        n.textContent = 'Sending it…';
        fetch(PROXY + '/offer_paylink', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ token: token() })
        }).then(function (r) { return r.json(); }).then(function (j) {
          j = j || {};
          n.className = 'puo__note ' + (j.ok ? 'puo__note--ok' : 'puo__note--bad');
          if (j.ok) {
            n.textContent = (j.message || 'On its way to your inbox.') +
              ' It carries this same Pay now link, and the unit stays held for you.';
          } else {
            // Never an error screen: the offer is untouched and Pay now is still
            // right there on this page.
            n.textContent = (j.message || 'We could not send that just now.') +
              ' The link on this page still works.';
            btn.disabled = false;
          }
        }).catch(function () {
          n.className = 'puo__note puo__note--bad';
          n.textContent = 'We could not send that just now. The link on this page ' +
            'still works, and your offer is unchanged.';
          btn.disabled = false;
        });
      });
    }
    if (secs != null) { payTick(secs); }
    if (OFFER) {
      wirePass(OFFER);
      if (takePassIntent()) { openPass(OFFER); }
    }
  }

  var t = token();
  if (!t) { render({ state: 'notfound' }); return; }
  fetch(PROXY + '/offer?t=' + encodeURIComponent(t), {
    headers: { 'Accept': 'application/json' }
  }).then(function (r) { return r.json(); })
    .then(function (j) { render(j || { state: 'error' }); })
    .catch(function () { render({ state: 'error' }); });
})();
